import { useState, useEffect, useRef } from 'react';
import { X, Upload, Image as ImageIcon, Trash2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useConfirm } from '../ui/ConfirmDialog';
import { toast } from '../ui/Toaster';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';

interface ImageGalleryProps {
  onClose: () => void;
  onSelectImage: (url: string) => void;
}

interface StoredImage {
  name: string;
  url: string;
  created_at: string;
}

export default function ImageGallery({ onClose, onSelectImage }: ImageGalleryProps) {
  const confirm = useConfirm();
  const [activeTab, setActiveTab] = useState<'upload' | 'gallery'>('gallery');
  const [images, setImages] = useState<StoredImage[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadImages();
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const loadImages = async () => {
    const { data, error } = await supabase.storage.from('email-images').list('', {
      limit: 100,
      sortBy: { column: 'created_at', order: 'desc' },
    });

    if (!error && data) {
      const imagesWithUrls = data
        .filter((file) => file.name !== '.emptyFolderPlaceholder')
        .map((file) => ({
          name: file.name,
          url: supabase.storage.from('email-images').getPublicUrl(file.name).data.publicUrl,
          created_at: file.created_at,
        }));
      setImages(imagesWithUrls);
    }
  };

  const handleFileSelect = (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Selecciona un archivo de imagen');
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const uploadImage = async () => {
    if (!selectedFile) return;

    setIsUploading(true);

    const fileName = `${Date.now()}-${selectedFile.name}`;
    const { error } = await supabase.storage
      .from('email-images')
      .upload(fileName, selectedFile, {
        cacheControl: '3600',
        upsert: false,
      });

    if (error) {
      toast.error('Error al subir imagen: ' + error.message);
    } else {
      const { data } = supabase.storage.from('email-images').getPublicUrl(fileName);
      onSelectImage(data.publicUrl);
      setSelectedFile(null);
      setPreviewUrl(null);
      loadImages();
      setActiveTab('gallery');
    }

    setIsUploading(false);
  };

  const deleteImage = async (imageName: string) => {
    const ok = await confirm({
      title: 'Eliminar imagen',
      description: '¿Seguro que deseas eliminar esta imagen de la galería?',
      confirmText: 'Eliminar',
      tone: 'danger',
    });
    if (!ok) return;

    const { error } = await supabase.storage.from('email-images').remove([imageName]);

    if (error) {
      toast.error('Error al eliminar imagen: ' + error.message);
    } else {
      toast.success('Imagen eliminada');
      loadImages();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="image-gallery-title"
    >
      <div className="glass-panel rounded-2xl border border-white/[0.06] w-full max-w-4xl max-h-[80vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/[0.06]">
          <h2 id="image-gallery-title" className="text-xl font-semibold text-white">
            Galería de Imágenes
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-white/[0.06] text-white/50 hover:text-white transition-colors"
            aria-label="Cerrar galería"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/[0.06]">
          {(['gallery', 'upload'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === tab
                  ? 'text-cubus-lime border-b-2 border-cubus-lime'
                  : 'text-white/50 hover:text-white'
              }`}
            >
              {tab === 'gallery' ? 'Galería' : 'Subir Nueva'}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'gallery' ? (
            images.length === 0 ? (
              <EmptyState
                icon={ImageIcon}
                title="Sin imágenes"
                description='Sube tu primera imagen en la pestaña "Subir Nueva".'
              />
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {images.map((image) => (
                  <div
                    key={image.name}
                    className="group relative aspect-square glass-surface border border-white/[0.06] rounded-xl overflow-hidden cursor-pointer hover:border-white/[0.15] transition-all"
                    onClick={() => {
                      onSelectImage(image.url);
                      onClose();
                    }}
                  >
                    <img
                      src={image.url}
                      alt={image.name}
                      className="w-full h-full object-cover"
                    />
                    {/* Delete button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteImage(image.name);
                      }}
                      className="absolute top-2 right-2 p-1.5 bg-rose-500/80 backdrop-blur-sm rounded-lg opacity-0 group-hover:opacity-100 transition-all hover:bg-rose-500"
                      aria-label="Eliminar imagen"
                    >
                      <Trash2 className="w-3.5 h-3.5 text-white" />
                    </button>
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                      <span className="text-white text-xs font-semibold bg-black/40 px-3 py-1 rounded-full">
                        Click para insertar
                      </span>
                    </div>
                    {/* Name bar */}
                    <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent pointer-events-none">
                      <p className="text-white text-xs truncate">{image.name}</p>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            /* Upload tab */
            <div className="space-y-4">
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-2xl p-12 text-center transition-all ${
                  isDragging
                    ? 'border-cubus-lime bg-cubus-lime/[0.06]'
                    : 'border-white/[0.12] hover:border-white/[0.25] hover:bg-white/[0.02]'
                }`}
              >
                <Upload className={`w-12 h-12 mx-auto mb-4 transition-colors ${isDragging ? 'text-cubus-lime' : 'text-white/30'}`} />
                <p className="text-white font-medium mb-1">Arrastra una imagen aquí</p>
                <p className="text-white/40 text-sm mb-4">o</p>
                <Button
                  variant="glass"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Seleccionar Archivo
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileSelect(file);
                  }}
                  className="hidden"
                />
              </div>

              {previewUrl && (
                <div className="glass-surface border border-white/[0.06] rounded-2xl p-4 space-y-4">
                  <p className="text-white font-medium text-sm">Vista Previa:</p>
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="max-w-full max-h-64 mx-auto rounded-xl"
                  />
                  <p className="text-white/50 text-xs text-center">{selectedFile?.name}</p>
                  <div className="flex gap-3">
                    <Button
                      variant="lime"
                      fullWidth
                      onClick={uploadImage}
                      loading={isUploading}
                    >
                      {isUploading ? 'Subiendo...' : 'Subir y Usar'}
                    </Button>
                    <Button
                      variant="glass"
                      onClick={() => {
                        setSelectedFile(null);
                        setPreviewUrl(null);
                      }}
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
